// hooks
import { useMemo } from 'react';

// utils
import { addMonths, endOfDay, isAfter, isBefore, startOfDay } from 'date-fns';
import { parseBackendDateTime, toDateString } from 'utils/date';
import { queries as rapiniQueries } from 'RestAPIs/rapini';
import { isDayCovered } from './overlapPeriods';

// types
import type { RegisterAbsenceWorkAgreement } from 'RestAPIs/roster/register-absence';

type UseOverlapDetectionArgs = {
  open: boolean;
  /** in edit mode the only overlap in reach is the absence being edited
   *  itself (the period can only shrink), so the check stays off there */
  isEdit: boolean;
  absenceId?: number;
  employeeId: number;
  startDate: Date | null;
  endDate: Date | null;
  hasValidPeriod: boolean;
  today: Date;
  workAgreements: RegisterAbsenceWorkAgreement[];
  selectedWaIds: number[];
};

/**
 * A registered absence overlapping the period makes the save pointless: the
 * backend refuses overlapping absences per work agreement. Detected live from
 * the same source as the overview, so the planner hears it before saving
 * instead of after.
 */
export const useOverlapDetection = ({
  open,
  isEdit,
  absenceId,
  employeeId,
  startDate,
  endDate,
  hasValidPeriod,
  today,
  workAgreements,
  selectedWaIds
}: UseOverlapDetectionArgs) => {
  // A month around the period, not the period itself: the date fields grey out
  // the days that are already covered, and the calendar shows a whole month at
  // a time. An absence just outside the period does not overlap it, but the
  // planner does need to see it while picking a date.
  const registeredAbsencesQuery = rapiniQueries.useGetAbsenceNotices(
    toDateString(addMonths(startDate ?? today, -1)),
    toDateString(addMonths(endDate ?? startDate ?? today, 1)),
    employeeId,
    { enabled: open && hasValidPeriod && !isEdit }
  );

  const overlappingAbsences = useMemo(
    () =>
      // Editing is judged here, not only by the disabled query above: a
      // disabled react-query still hands back cached data, and the register
      // dialog fills exactly this cache for the same employee and period. The
      // absence being edited is itself a registered absence, so that cache hit
      // would make the dialog report an overlap with itself — a red message,
      // its own duties greyed out as "already covered", and a save it blocks.
      startDate && endDate && !isEdit
        ? (registeredAbsencesQuery.data ?? []).filter(
            (notice) =>
              notice.status === 'REGISTERED' &&
              // never itself, whatever the mode
              notice.absenceId !== absenceId &&
              isBefore(parseBackendDateTime(notice.start), endOfDay(endDate)) &&
              isAfter(parseBackendDateTime(notice.end), startOfDay(startDate))
          )
        : [],
    [registeredAbsencesQuery.data, startDate, endDate, isEdit, absenceId]
  );

  // wa numbers ("1" or "1, 2") of the overlapping absences; a selected work
  // agreement in that set means the backend will reject the whole save
  const overlappingWaNrs = useMemo(
    () =>
      new Set(
        overlappingAbsences.flatMap(
          (notice) =>
            notice.workAgreement
              ?.split(',')
              .map((nr) => nr.trim())
              .filter(Boolean) ?? []
        )
      ),
    [overlappingAbsences]
  );
  const hasBlockingOverlap = workAgreements.some(
    (workAgreement) =>
      selectedWaIds.includes(workAgreement.waId) &&
      overlappingWaNrs.has(String(workAgreement.nr))
  );

  /**
   * Every registered absence the query returned, so also the ones that only
   * touch the period. Work agreements resolved to ids: an absence on a work
   * agreement outside this period drops out, since greying or bounding by date
   * alone would hit another work agreement that is perfectly able to work.
   */
  const nearbyAbsencePeriods = useMemo(
    () =>
      (isEdit ? [] : registeredAbsencesQuery.data ?? [])
        .filter(
          (notice) =>
            notice.status === 'REGISTERED' && notice.absenceId !== absenceId
        )
        .map((notice) => {
          /** work agreement numbers of the notice, as the overview writes them */
          const waNrs = new Set(
            notice.workAgreement
              ?.split(',')
              .map((nr) => nr.trim())
              .filter(Boolean) ?? []
          );
          return {
            start: startOfDay(parseBackendDateTime(notice.start)),
            end: startOfDay(parseBackendDateTime(notice.end)),
            waIds: workAgreements
              .filter((workAgreement) => waNrs.has(String(workAgreement.nr)))
              .map((workAgreement) => workAgreement.waId)
          };
        })
        .filter((period) => period.waIds.length > 0),
    [registeredAbsencesQuery.data, workAgreements, isEdit, absenceId]
  );

  /** the ones the consequences panel greys out: inside the period, on a work
   *  agreement this notice includes */
  const coveredAbsencePeriods = useMemo(
    () =>
      nearbyAbsencePeriods.filter(
        (period) =>
          !!startDate &&
          !!endDate &&
          isBefore(period.start, endOfDay(endDate)) &&
          isAfter(endOfDay(period.end), startOfDay(startDate))
      ),
    [nearbyAbsencePeriods, startDate, endDate]
  );

  // only absences on a work agreement this notice includes constrain it: one on
  // a work agreement left out is reported, but bounding the dates for it would
  // take away days the planner may legitimately use
  const constrainingPeriods = nearbyAbsencePeriods.filter((period) =>
    period.waIds.some((waId) => selectedWaIds.includes(waId))
  );

  /**
   * Is this day already covered by a registered absence of a work agreement
   * this notice includes?
   *
   * The period is deliberately left where the planner put it, even when it
   * overlaps: moving it out of the way would take the overlap off the screen,
   * and with it the message and the greyed rows that are the only sign the
   * other absence exists. The date fields grey these days out instead, so the
   * planner sees which days are taken while picking, and the message says why
   * saving is refused.
   */
  const isRegisteredAbsenceDay = (date: Date) =>
    isDayCovered(date, constrainingPeriods);

  return {
    overlappingAbsences,
    hasBlockingOverlap,
    nearbyAbsencePeriods,
    coveredAbsencePeriods,
    constrainingPeriods,
    isRegisteredAbsenceDay
  };
};
